import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Warning from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { FormTextField } from "components/Forms/FormTextField";
import { VerifierFragmentFragment, Issue } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import { useMemo, useState, useCallback } from "react";
import { VerifierTable } from "./VerifierTable";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { useBulkVerifyInputValidation } from "routes/Staff/Fulfillment/validationHooks/useBulkVerifyInputValidation";
import { useValidatingForm } from "hooks/useValidatingForm";
import { IssueListButton } from "./Forms/IssueListButton";

export type VerifyAndSubmitFormState = {
  personId?: string;
  verifierFullName: string;
  verifierTitle: string;
  verifierPhone: string;
  verifierEmail: string;
  date: string;
};

type BaseProps = {
  onClose: () => void;
  open: boolean;
  loading: boolean;
  defaultVerifier?: {
    person?: VerifierFragmentFragment;
    fullName?: string;
    title?: string;
  };
  isReportKindAnnual: boolean;
  reportIds?: string[];
};

type Props = BaseProps &
  (
    | {
        onSubmit: (formState: VerifyAndSubmitFormState) => Promise<void>;
        withConfirmation?: false;
      }
    | {
        onSubmit: (
          formState: VerifyAndSubmitFormState,
          overrideOpenReviews: boolean
        ) => Promise<void>;
        withConfirmation: true;
        skippedDueToOpenReviewsCount: number;
        skippedDueToVerificationCount: number;
        verifiableRowsWithoutOpenReviewsCount: number;
        reportsWithValidationErrorsCount: number;
      }
  );

export const VerifyAndSubmitFormDialog = (props: Props) => {
  const theme = useTheme();
  const validateInput = useBulkVerifyInputValidation();

  const defaultDate = useMemo(() => {
    const currentDate = DateTime.now();
    const comparisonDate = DateTime.fromISO(
      `${currentTierIIReportingYear + 1}-01-01`
    );

    if (currentDate < comparisonDate && props.isReportKindAnnual) {
      return comparisonDate.toISODate();
    } else {
      return currentDate.toISODate();
    }
  }, [props.isReportKindAnnual]);

  const [person, setPerson] = useState<VerifierFragmentFragment | null>(
    props.defaultVerifier?.person ?? null
  );

  const [showConfirmation, setShowConfirmation] = useState(false);

  // Create validator function for useValidatingForm
  const validator = useCallback(
    async (formData: VerifyAndSubmitFormState): Promise<Issue[]> => {
      if (!props.reportIds || props.reportIds.length === 0) {
        return [];
      }

      const verifierData = {
        verifiedAt: formData.date,
        verifierFullName: formData.verifierFullName,
        verifierTitle: formData.verifierTitle,
        verifierEmail: formData.verifierEmail,
        verifierPhone: formData.verifierPhone,
      };

      return await validateInput(props.reportIds, verifierData);
    },
    [props.reportIds, validateInput]
  );

  const defaultFormData = useMemo(
    () => ({
      personId: person?.id ?? undefined,
      verifierFullName:
        person?.first && person?.last
          ? `${person?.first} ${person?.last}`
          : props.defaultVerifier?.fullName ?? "",
      verifierTitle: person?.title ?? props.defaultVerifier?.title ?? "",
      verifierPhone: person?.phones?.[0]?.number ?? "",
      verifierEmail: person?.email ?? "",
      date: defaultDate,
    }),
    [person, props.defaultVerifier, defaultDate]
  );

  const form = useValidatingForm(defaultFormData, undefined, validator);

  const handleClose = () => {
    if (props.withConfirmation) {
      setShowConfirmation(false);
    }
    props.onClose();
    setPerson(null);
    form.reset();
  };

  const onVerify = async (formState: VerifyAndSubmitFormState) => {
    if (props.withConfirmation) {
      setShowConfirmation(true);
    } else {
      await props.onSubmit(formState);
      handleClose();
    }
  };
  const onClearPerson = () => {
    setPerson(null);
    form.reset(defaultFormData);
  };

  const onSelectPerson = (person: VerifierFragmentFragment | null) => {
    setPerson(person);
    form.setValue("personId", person?.id);
    form.setValue(
      "verifierFullName",
      person?.first && person?.last ? `${person?.first} ${person?.last}` : ""
    );
    form.setValue("verifierTitle", person?.title ?? "");
    form.setValue("verifierPhone", person?.phones?.[0]?.number ?? "");
    form.setValue("verifierEmail", person?.email ?? "");
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth="lg"
      title={
        <Box display="flex">
          Verify & Submit
          <Tooltip title="Use the verifier table to find a verifier, OR enter one manually">
            <IconButton size="small" style={{ marginLeft: theme.spacing(1) }}>
              <InfoOutlined fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      }
      primaryActionButtonProps={{
        onClick: form.handleSubmit(onVerify),
        disabled: form.formState.isSubmitting || form.issues.length > 0,
        loading: props.loading,
        saveText: "Verify & Submit",
      }}
      additionalPrimaryActionElements={[
        <>
          {form.issues.length > 0 && <IssueListButton issues={form.issues} />}
        </>,
        <Button key="clear-person" onClick={onClearPerson} variant="outlined">
          Clear Person
        </Button>,
      ]}
    >
      <Grid container spacing={1}>
        <Grid item xs={12} md={6} lg={4.8}>
          <FormTextField
            name="verifierFullName"
            label="Full Name"
            control={form.control}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <FormTextField
            name="verifierTitle"
            label="Title"
            control={form.control}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <FormTextField
            name="verifierPhone"
            label="Phone"
            control={form.control}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <FormTextField
            name="verifierEmail"
            label="Email"
            control={form.control}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <DateField name="date" label="Verified On" control={form.control} />
        </Grid>
      </Grid>

      <VerifierTable onSelectPerson={onSelectPerson} />

      {props.withConfirmation && showConfirmation && (
        <ConfirmationDialog
          loading={props.loading}
          onClose={() => setShowConfirmation(false)}
          onSubmit={(overrideOpenReviews) =>
            form.handleSubmit(async (formState) => {
              await props.onSubmit(formState, overrideOpenReviews);
              handleClose();
            })()
          }
          skippedDueToOpenReviewsCount={props.skippedDueToOpenReviewsCount}
          skippedDueToVerificationCount={props.skippedDueToVerificationCount}
          verifiableRowsWithoutOpenReviewsCount={
            props.verifiableRowsWithoutOpenReviewsCount
          }
          reportsWithValidationErrorsCount={
            props.reportsWithValidationErrorsCount
          }
        />
      )}
    </Dialog>
  );
};

type ConfirmationProps = {
  loading: boolean;
  onClose: () => void;
  onSubmit: (overrideOpenReviews: boolean) => Promise<void>;
  skippedDueToOpenReviewsCount: number;
  skippedDueToVerificationCount: number;
  verifiableRowsWithoutOpenReviewsCount: number;
  reportsWithValidationErrorsCount: number;
};

const ConfirmationDialog = (props: ConfirmationProps) => {
  const [overrideOpenReviews, setOverrideOpenReviews] = useState(false);

  const aboutToVerifyCount =
    props.verifiableRowsWithoutOpenReviewsCount +
    (overrideOpenReviews ? props.skippedDueToOpenReviewsCount : 0);

  return (
    <Dialog
      open
      onClose={props.onClose}
      title="Are you sure?"
      primaryActionButtonProps={{
        loading: props.loading,
        disabled: aboutToVerifyCount === 0,
        onClick: () => props.onSubmit(overrideOpenReviews),
        saveText: "Yes, I'm sure",
      }}
    >
      <p>
        You are about to verify <b>{aboutToVerifyCount}</b>{" "}
        {pluralize("report", aboutToVerifyCount)}.
      </p>
      {!!props.skippedDueToVerificationCount && (
        <p>
          <b>{props.skippedDueToVerificationCount}</b>{" "}
          {pluralize("report", props.skippedDueToVerificationCount)}{" "}
          {pluralize("was", props.skippedDueToVerificationCount)} skipped
          because {props.skippedDueToVerificationCount > 1 ? "they" : "it"}{" "}
          {props.skippedDueToVerificationCount > 1 ? "are" : "is"} not awaiting
          verification.
        </p>
      )}
      {!!props.skippedDueToOpenReviewsCount && (
        <>
          {!overrideOpenReviews && (
            <p>
              <b>{props.skippedDueToOpenReviewsCount}</b>{" "}
              {pluralize("report", props.skippedDueToOpenReviewsCount)}{" "}
              {pluralize("was", props.skippedDueToOpenReviewsCount)} skipped
              because {props.skippedDueToOpenReviewsCount > 1 ? "they" : "it"}{" "}
              {props.skippedDueToOpenReviewsCount > 1 ? "have" : "has"} pending
              reviews.
            </p>
          )}
          <FormControlLabel
            sx={{ marginLeft: "0px" }}
            control={
              <Checkbox
                sx={{ padding: "9px 4px 9px 0px" }}
                checked={overrideOpenReviews}
                onChange={() => setOverrideOpenReviews(!overrideOpenReviews)}
              />
            }
            label={
              <Typography fontSize={14} fontWeight={500}>
                I want to override this safety check and include{" "}
                {props.skippedDueToOpenReviewsCount}{" "}
                {pluralize("report", props.skippedDueToOpenReviewsCount)} with
                pending reviews.
              </Typography>
            }
          />
        </>
      )}

      {props.reportsWithValidationErrorsCount > 0 && (
        <Stack direction="row" gap={1}>
          <Warning color="error" />
          <Typography>
            <b>{props.reportsWithValidationErrorsCount}</b>{" "}
            {pluralize("report", props.reportsWithValidationErrorsCount)}{" "}
            {pluralize("has", props.reportsWithValidationErrorsCount)}{" "}
            validation errors.
          </Typography>
        </Stack>
      )}
    </Dialog>
  );
};
